import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useSearchParams, Link } from 'react-router'
import { FaEllipsisH } from 'react-icons/fa'
import { Box, Stack, Loading, Heading, Text, Select, Icon, Button, SimpleTable, Badge, EmptyBox, ConfirmButton, SimpleModal, Link as ChakraLink, Menu, MenuItem, SubMenu, MenuSeparator, SearchableSelect } from '@/components/lib'
import api from '@/api'
import useStore from '@/store'
import useTitle from '@/hooks/useTitle'
import utils from '@/utils/utils'

import DataProcessingImage from '@/images/dataProcessing.png'
import QuestionnaireResponseModal from '@/components/includes/QuestionnaireResponseModal'

const POLL_PARTICIPANT_ID = '000000000000000000000001'

export default function PollResponses () {
  const [loading, setLoading] = useState(false)
  const [selectedStage, setSelectedStage] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [responses, setResponses] = useState([])
  const [responseValidations, setResponseValidations] = useState([])
  const [queries, setQueries] = useState([])
  const [selectedResponse, setSelectedResponse] = useState(null)
  const [manualAllocateResponseId, setManualAllocateResponseId] = useState(null)
  const [manualAllocateParticipantId, setManualAllocateParticipantId] = useState('')
  const [manualAllocateParticipants, setManualAllocateParticipants] = useState([])
  const [manualAllocateLoading, setManualAllocateLoading] = useState(false)
  const { id } = useParams()
  // Deep links name the stage and
  // the response to open: /poll-responses?stage=<id>&response=<id>
  const [searchParams] = useSearchParams()
  const linkedStageId = searchParams.get('stage')
  const linkedResponseId = searchParams.get('response')
  const linkedDataTypeId = searchParams.get('dataType')
  const { user, trial, stages } = useStore()
  const canReadPii = utils.canReadParticipantPersonalInformation(user, trial)
  const canExport = utils.canExportData(user, trial)
  useTitle('Poll Responses', trial)

  const questionnaireStages = useMemo(() => {
    return stages?.filter(stage => stage.isPoll) || []
  }, [stages])

  // Responses that came from a consent form's "continue" button carry the
  // consent they followed. The column only appears once at least one response
  // has one, so a questionnaire nobody was handed off to looks as it did.
  const hasLinkedConsents = useMemo(() => {
    return responses.some(response => response.trialConsent)
  }, [responses])

  function renderConsentCell (response) {
    if (!response.trialConsent) return <Badge>No consent linked</Badge>
    const consent = response.trialConsentObject
    if (!consent) {
      // Linked, but the API withholds the consent itself from anyone without
      // permission to read the trial's consents.
      return <Badge colorPalette='green'>Consent linked</Badge>
    }
    return (
      <Stack gap={1}>
        <Badge colorPalette='green'>{consent.fullName}</Badge>
        {consent.fileUrl && (
          <ChakraLink asChild variant='underline'>
            <a href={consent.fileUrl} target='_blank' rel='noreferrer'>Signed form</a>
          </ChakraLink>
        )}
      </Stack>
    )
  }
  const selectedStageObject = useMemo(() => {
    return questionnaireStages.find(stage => stage._id === selectedStage)
  }, [selectedStage, questionnaireStages])
  if (questionnaireStages.length > 0 && !selectedStage) {
    const linked = questionnaireStages.find(stage => stage._id === linkedStageId)
    setSelectedStage((linked || questionnaireStages[0])._id)
  }

  useEffect(() => {
    if (!selectedStage) return
    setLoading(true)
    api.trials.getStageResponses(trial._id, selectedStage, 'json', data => {
      setLoading(false)
      setResponses(data.responses || [])
      setResponseValidations(data.responseValidations || [])
      setQueries(data.queries || [])
      // Open the linked submission once it has loaded, but only on the stage it
      // belongs to — switching stage afterwards shouldn't reopen it.
      if (linkedResponseId && (data.responses || []).some(r => r._id === linkedResponseId)) {
        setSelectedResponse(linkedResponseId)
      }
    })
  }, [id, selectedStage, linkedResponseId])

  const deleteResponse = (responseId) => {
    api.trials.deleteResponse(trial._id, responseId, () => {
      utils.toast('success', 'Response deleted')
      setResponses(responses.filter(r => r._id !== responseId))
    }, err => utils.toast('error', 'Unable to delete response', err.message))
  }

  const setResponseStatus = (responseId, nextStatus) => {
    api.trials.setResponseStatus(trial._id, responseId, nextStatus, (updated) => {
      setResponses(prev => prev.map(r => {
        if (r._id !== updated._id) return r
        const next = { ...r, ...updated }
        if (!updated.status) delete next.status // When removing status
        return next
      }))
      utils.toast('success', 'Response status updated')
    }, err => utils.toast('error', 'Unable to update response status', err.message))
  }

  const allocateResponseToParticipant = (responseId) => {
    api.trials.allocateResponseToUnallocatedParticipant(trial._id, responseId, (newResponse) => {
      utils.toast('success', 'Response auto-allocated to participant')
      setResponses(responses.map(r => r._id === responseId ? newResponse : r))
    }, err => {
      utils.toast('error', 'Unable to auto-allocate response', err.message)
    })
  }

  const deallocateResponseFromParticipant = (responseId) => {
    api.trials.deallocateResponseFromParticipant(trial._id, responseId, (newResponse) => {
      utils.toast('success', 'Response de-allocated')
      setResponses(responses.map(r => r._id === responseId ? newResponse : r))
    }, err => {
      utils.toast('error', 'Unable to de-allocate response', err.message)
    })
  }

  const openManualAllocate = (responseId) => {
    setManualAllocateResponseId(responseId)
    setManualAllocateParticipantId('')
    setManualAllocateParticipants([])
  }

  const closeManualAllocate = () => {
    setManualAllocateResponseId(null)
    setManualAllocateParticipantId('')
    setManualAllocateParticipants([])
    setManualAllocateLoading(false)
  }

  const allocateResponseToSelectedParticipant = () => {
    if (!manualAllocateResponseId || !manualAllocateParticipantId) return
    api.trials.allocateResponseToUnallocatedParticipant(
      trial._id,
      manualAllocateResponseId,
      (newResponse) => {
        utils.toast('success', 'Response allocated to participant')
        setResponses(prev => prev.map(r => r._id === manualAllocateResponseId ? newResponse : r))
        closeManualAllocate()
      },
      err => utils.toast('error', 'Unable to allocate response', err.message),
      { participant: manualAllocateParticipantId }
    )
  }

  const filteredResponses = useMemo(() => {
    return responses.filter(response => {
      if (selectedStatus && response.status !== selectedStatus) return false
      return true
    })
  }, [responses, selectedStatus])

  const exportResponses = () => {
    api.trials.getStageResponses(trial._id, selectedStage, 'csv', data => {
      utils.downloadFile(`data:text/csv;charset=utf-8,${encodeURIComponent(data.csv)}`, `${trial.name || 'trial'}-stage-${selectedStageObject.name || selectedStage}-responses.csv`)
      utils.toast('success', 'CSV file generated successfully', 'It should now be in your downloads folder.')
    }, err => {
      utils.toast('error', 'Unable to export responses', err.message)
    })
  }

  return (
    <Stack gap={5}>
      <Box display={{ md: 'flex' }} justifyContent='space-between'>
        <Box>
          <Heading as='h2' size='lg'>Questionnaire responses</Heading>
          <Text>Responses made to questionnaire stages.</Text>
        </Box>
        <Stack direction={{ sm: 'row' }}>
          <Box w='250px'>
            <Select value={selectedStage} onChange={e => setSelectedStage(e.target.value)}>
              {questionnaireStages.map(stage => (
                <option key={stage._id} value={stage._id}>
                  {stage.name || 'Unnamed Stage'}
                </option>
              ))}
            </Select>
          </Box>
          <Select w='150px' value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
            <option value=''>All statuses</option>
            <option value='successful'>Successful</option>
            <option value='unsuccessful'>Failed</option>
          </Select>
          {selectedStage && canExport &&
            <Button onClick={exportResponses} colorPalette='blue'>
              Export Responses
            </Button>}
        </Stack>
      </Box>

      {loading && <Loading />}
      {!loading && responses.length === 0 && (
        <EmptyBox
          image={DataProcessingImage}
          title='This questionnaire stage has no responses'
          description='Responses will display here once participants have completed the questionnaire.'
        />
      )}

      {!loading && responses.length > 0 && filteredResponses.length === 0 && (
        <EmptyBox
          image={DataProcessingImage}
          title='No responses match the selected filters'
          description='Try changing the filters to see more responses.'
        />
      )}

      {!loading && filteredResponses.length > 0 && (
        <SimpleTable
          striped asCard
          onRowClick={(row, index) => setSelectedResponse(filteredResponses[index]._id)}
          headers={[
            'Date',
            'Score',
            'Status',
            canReadPii && selectedStageObject.pollSettings?.requireEmail ? 'Email' : null,
            canReadPii && selectedStageObject.pollSettings?.requirePhone ? 'Phone' : null,
            hasLinkedConsents ? 'Consent' : null,
            'Participant ID',
            ''
          ]}
          rows={filteredResponses.map(response => {
            const isUnallocated = !response.participant || response.participant === POLL_PARTICIPANT_ID
            const canManage = utils.canManageTrial(user, trial)
            return [
              utils.formatDateTime(response.createdAt),
              (response.score || response.score === 0) ? <Badge colorPalette='blue'>{response.score}</Badge> : null,
              response.status &&
                <Badge colorPalette={response.status === 'successful' ? 'green' : 'red'}>{response.status}</Badge>,
              canReadPii && selectedStageObject.pollSettings?.requireEmail ? <ChakraLink asChild variant='underline'><a href={`mailto:${response.email}`}>{response.email}</a></ChakraLink> : null,
              canReadPii && selectedStageObject.pollSettings?.requirePhone ? response.phone : null,
              hasLinkedConsents ? renderConsentCell(response) : null,
              isUnallocated
                ? <Badge colorPalette='yellow'>Unallocated</Badge>
                : <ChakraLink asChild variant='underline' fontFamily='monospace'><Link to={`/trials/${trial._id}/participants/${response.participant}`}>{response.participantId}</Link></ChakraLink>,
              <Stack direction='row' key={response._id} display='flex' justifyContent='end'>
                <Button size='sm' onClick={() => setSelectedResponse(response._id)}>View</Button>
                {isUnallocated && canManage && (
                  <ConfirmButton
                    onConfirm={() => allocateResponseToParticipant(response._id)}
                    header='Auto-allocate this response to a participant'
                    body='If available, this response will be allocated to the next available unallocated participant.'
                    trigger={<Button size='sm' variant='outline'>Auto-allocate</Button>}
                  />
                )}
                <Menu
                  closeOnSelect
                  trigger={
                    <Button size='sm' variant='ghost' disabled={!canManage}>
                      <Icon><FaEllipsisH /></Icon>
                    </Button>
                  }
                >
                  <SubMenu trigger='Status'>
                    <MenuItem value='neither' onClick={() => setResponseStatus(response._id, '')}>Unset</MenuItem>
                    <MenuSeparator />
                    <MenuItem value='successful' onClick={() => setResponseStatus(response._id, 'successful')}>Successful</MenuItem>
                    <MenuItem value='unsuccessful' onClick={() => setResponseStatus(response._id, 'unsuccessful')}>Unsuccessful</MenuItem>
                  </SubMenu>
                  <MenuSeparator />
                  {!isUnallocated && (
                    <MenuItem value='deallocate' onClick={() => deallocateResponseFromParticipant(response._id)}>
                      De-allocate
                    </MenuItem>
                  )}
                  {isUnallocated && canManage && (
                    <MenuItem value='allocate' onClick={() => openManualAllocate(response._id)}>
                      Allocate…
                    </MenuItem>
                  )}
                  <ConfirmButton
                    asSubMenu
                    onConfirm={() => deleteResponse(response._id)}
                    header='Really delete this response?'
                    body='This action cannot be undone and will permanently delete the response.'
                    trigger='Delete'
                    actionColor='red'
                  />
                </Menu>
              </Stack>
            ]
          })}
        />
      )}
      <QuestionnaireResponseModal
        isOpen={!!selectedResponse}
        onClose={() => setSelectedResponse(null)}
        response={responses.find(r => r._id === selectedResponse)}
        responseValidations={responseValidations}
        setResponseValidations={setResponseValidations}
        queries={queries}
        setQueries={setQueries}
        canReadPii={canReadPii}
        highlightDataTypeId={selectedResponse === linkedResponseId ? linkedDataTypeId : null}
      />
      <ManualAllocateModal
        isOpen={!!manualAllocateResponseId}
        onClose={closeManualAllocate}
        participantId={manualAllocateParticipantId}
        setParticipantId={setManualAllocateParticipantId}
        participants={manualAllocateParticipants}
        setParticipants={setManualAllocateParticipants}
        loading={manualAllocateLoading}
        setLoading={setManualAllocateLoading}
        onAllocate={allocateResponseToSelectedParticipant}
        trial={trial}
      />
    </Stack>
  )
}

function ManualAllocateModal ({
  isOpen,
  onClose,
  participantId,
  setParticipantId,
  participants,
  setParticipants,
  loading,
  setLoading,
  onAllocate,
  trial
}) {
  const participantOptions = useMemo(() => {
    return (participants || []).map(p => ({
      value: p._id,
      label: p.id || p._id
    }))
  }, [participants])

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    api.trials.getParticipants(
      trial._id,
      1,
      '',
      '',
      '',
      'id',
      'asc',
      { allocationUnallocated: true },
      data => {
        setLoading(false)
        setParticipants(data.participants || [])
      },
      err => {
        setLoading(false)
        utils.toast('error', 'Unable to load unallocated participants', err.message)
      }
    )
  }, [isOpen, trial?._id])

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      header='Allocate response to a participant'
      size='md'
      body={
        <Stack gap={3}>
          <Box>
            <Text fontWeight='600' mb={1}>Choose a participant</Text>
            <SearchableSelect
              options={participantOptions}
              value={participantId}
              onChange={setParticipantId}
              placeholder={loading ? 'Loading...' : 'Find a participant...'}
              emptyMessage={loading ? 'Loading...' : 'No unallocated participants found'}
              w='100%'
            />
          </Box>
          <Text fontSize='sm' color='fg.muted'>Allocating will also copy any contact details from the response into the participant's profile.</Text>
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='end'>
          <Button variant='ghost' onClick={onClose}>Cancel</Button>
          <Button
            colorPalette='primary'
            onClick={onAllocate}
            disabled={!participantId || loading}
          >
            Allocate
          </Button>
        </Stack>
      }
    />
  )
}
